import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface IndustryCardProps {
  industry: {
    id: string;
    name: string;
    icon: string;
    description: string;
    image: string;
    alt: string;
    challenges: string[];
    solutions: string[];
    caseStudyCount: number;
  };
  onLearnMore: (id: string) => void;
}

const IndustryCard = ({ industry, onLearnMore }: IndustryCardProps) => {
  return (
    <div className="bg-card rounded-lg shadow-md hover:shadow-partnership transition-all duration-base overflow-hidden group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={industry.image}
          alt={industry.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-slow"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-transparent to-transparent opacity-60" />
        <div className="absolute bottom-4 left-4 flex items-center space-x-3">
          <div className="bg-accent p-3 rounded-lg">
            <Icon name={industry.icon as any} size={24} variant="solid" className="text-accent-foreground" />
          </div>
          <h3 className="text-2xl font-headline font-bold text-white">
            {industry.name}
          </h3>
        </div>
      </div>
      
      <div className="p-6">
        <p className="text-muted-foreground font-body mb-6 leading-relaxed">
          {industry.description}
        </p>
        
        <div className="space-y-4 mb-6">
          <div>
            <h4 className="text-sm font-headline font-semibold text-foreground mb-3 flex items-center">
              <Icon name="ExclamationTriangleIcon" size={16} variant="outline" className="text-warning mr-2" />
              Key Challenges
            </h4>
            <ul className="space-y-2">
              {industry.challenges.slice(0, 3).map((challenge, index) => (
                <li key={index} className="text-sm text-muted-foreground flex items-start">
                  <Icon name="ChevronRightIcon" size={16} variant="outline" className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                  <span>{challenge}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-headline font-semibold text-foreground mb-3 flex items-center">
              <Icon name="CheckCircleIcon" size={16} variant="outline" className="text-success mr-2" />
              Our Solutions
            </h4>
            <ul className="space-y-2">
              {industry.solutions.slice(0, 3).map((solution, index) => (
                <li key={index} className="text-sm text-muted-foreground flex items-start">
                  <Icon name="ChevronRightIcon" size={16} variant="outline" className="text-accent mr-2 mt-0.5 flex-shrink-0" />
                  <span>{solution}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="DocumentTextIcon" size={16} variant="outline" />
            <span>{industry.caseStudyCount} Case Studies</span>
          </div>
          
          <button
            onClick={() => onLearnMore(industry.id)}
            className="px-4 py-2 bg-primary text-primary-foreground font-headline font-semibold text-sm rounded-md hover:bg-opacity-90 transition-all duration-base flex items-center space-x-2"
          >
            <span>Learn More</span>
            <Icon name="ArrowRightIcon" size={16} variant="outline" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndustryCard;